import { seedApiQueryData, useApiQuery, type Api } from "../../../services/api";
import { sortArticleImages } from "../utils/sortArticleImages";
import { useArticleSwipe } from "./useArticleSwipe";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { useFirebaseUser } from "../../../domains/user/hooks/useFirebaseUser";

type ArticleListItem =
  Api["get"]["/webapp/public/articles"]["output"]["body"]["docs"][number];
export type ArticleDetail =
  Api["get"]["/webapp/public/articles/:slug"]["output"]["body"];
export type RelatedArticleCard =
  Api["get"]["/webapp/public/articles/:slug/related"]["output"]["body"][number];
export type ArticlePageArticle = ArticleDetail | ArticleListItem;

/**
 * A room the reader is not in, offered from the article. `join` when the reader
 * followed a link into that very room (they join it where they stand), and
 * `discover` when the room merely carries the article.
 */
export type DiscoverableRoomOffer = {
  room: Api["get"]["/webapp/private/articles/:id/discoverable-rooms"]["output"]["body"][number];
  action: "join" | "discover";
};

/**
 * Seed the article-detail query cache from a feed/list payload before
 * navigating to the article page. Feed docs are the same full `Article`
 * serialization as the detail endpoint (minus a hydrated
 * `associatedContent`), so the page renders instantly from the seed while
 * react-query refetches the authoritative detail in the background.
 */
export function seedArticleDetailCache(
  article: ArticleDetail,
  roomId: string | null
): void {
  seedApiQueryData(
    "/webapp/public/articles/:slug",
    { param: { slug: article.slug }, query: articleDetailQuery(roomId) },
    { status: 200, headers: {}, body: article }
  );
}

/**
 * The detail query names the room the article is read in, because the api
 * withholds the appraisal inside a client's room. The public app names none.
 * Every cached reader of this endpoint builds its query here, so they share one
 * key.
 */
export function articleDetailQuery(roomId: string | null) {
  return roomId === null ? {} : { roomId };
}

export function useArticleData(params: {
  slug: string;
  roomId: string | null;
}): {
  article: ArticlePageArticle | null;
  isLoadingArticle: boolean;
  articleError: Error | null;
  articleImages: ArticlePageArticle["images"];
  relatedArticles: RelatedArticleCard[];
  isLoadingRelatedArticles: boolean;
  discoverableRoom: DiscoverableRoomOffer | null;
  isLoadingDiscoverableRooms: boolean;
  isDiscoverableRoomJoinable: boolean;
} {
  const { currentSwipeArticle } = useArticleSwipe(params.slug);
  const { contentLang } = useActiveLang();
  const { isAuthenticatedFirebase } = useFirebaseUser();
  const articleQuery = useApiQuery(
    "/webapp/public/articles/:slug",
    params.slug.length > 0
      ? {
          param: { slug: params.slug },
          query: articleDetailQuery(params.roomId),
        }
      : false
  );
  const fetchedArticle =
    articleQuery.state === "success" ? articleQuery.body : null;
  const isLoadingArticle = articleQuery.state === "pending";
  const articleError =
    articleQuery.state === "error" ? articleQuery.error : null;

  // Use swipe cache or fetched article
  const article = fetchedArticle ?? currentSwipeArticle ?? null;

  // Derived values. `origin: "AiGenerated"` images are covers minted from the
  // title for social share previews (see the api's `ensureArticleHasShareImage`),
  // not figures extracted from the paper — they must never render as figures.
  const articleImages =
    article !== null && article.images !== undefined
      ? sortArticleImages(
          article.images.filter((image) => image.origin !== "AiGenerated")
        )
      : [];

  // Related articles — server filters by the source article's own tags
  // (child concepts of specialties) so niche-topic rooms surface tightly
  // related content instead of anything sharing the broad parent specialty.
  const relatedArticlesQuery = useApiQuery(
    "/webapp/public/articles/:slug/related",
    params.slug.length > 0
      ? {
          param: { slug: params.slug },
          query: {
            language: contentLang,
            limit: 5,
          },
          durable: true,
        }
      : false
  );
  const relatedArticles =
    relatedArticlesQuery.state === "success" ? relatedArticlesQuery.body : [];
  const isLoadingRelatedArticles = relatedArticlesQuery.state === "pending";

  // Discoverable rooms
  const discoverableRoomsQuery = useApiQuery(
    "/webapp/private/articles/:id/discoverable-rooms",
    isAuthenticatedFirebase && article?.id
      ? {
          param: { id: article.id },
          durable: true,
        }
      : false
  );
  const discoverableRooms =
    discoverableRoomsQuery.state === "success"
      ? discoverableRoomsQuery.body
      : [];
  const isLoadingDiscoverableRooms = discoverableRoomsQuery.state === "pending";
  // A room in the URL is where the link came from, not proof of membership: the
  // server already leaves out every room the reader is in. So on a room article
  // the only room worth offering is that one, and a colleague's link into a room
  // the reader could join is exactly when to offer it. Anywhere else, the newest
  // room carrying the article.
  const offeredRoom =
    params.roomId === null
      ? discoverableRooms.at(0)
      : discoverableRooms.find((room) => room.id === params.roomId);
  const discoverableRoom: DiscoverableRoomOffer | null =
    offeredRoom === undefined
      ? null
      : {
          room: offeredRoom,
          action: params.roomId === null ? "discover" : "join",
        };
  const joinableQuery = useApiQuery(
    "/webapp/private/rooms/:id/join/check",
    isAuthenticatedFirebase && offeredRoom !== undefined
      ? { param: { id: offeredRoom.id }, durable: true }
      : false
  );
  const isDiscoverableRoomJoinable =
    joinableQuery.state === "success" && joinableQuery.body;

  return {
    article,
    isLoadingArticle,
    articleError,
    articleImages,
    relatedArticles,
    isLoadingRelatedArticles,
    discoverableRoom,
    isLoadingDiscoverableRooms,
    isDiscoverableRoomJoinable,
  };
}
